import type { AuthSmsLoginRes, UserVo } from '@/api/auth/types.ts'
import { useRoute, useRouter } from 'vue-router'
import { authAccessToken, authLogout, authPasswordLogin, authSmsLogin, getProfileApi } from '@/api/auth'
import { getCookie, removeCookie, setCookie } from '@/utils/cookie'

export const useUserStore = defineStore(
  // 唯一ID
  'user',
  () => {
    // 所有用户信息从 cookie 中读取，实现子域名共享
    const account = ref(getCookie('account') ?? '')
    const token = ref(getCookie('satoken') ?? '')
    const avatar = ref(getCookie('avatar') ?? '')
    const nickname = ref(getCookie('nickname') ?? '')
    const userId = ref(getCookie('userId') ?? '')
    const expireTime = ref(getCookie('expireTime') ?? '') // token有效期（秒）
    const loginTime = ref(getCookie('loginTime') ?? '') // 登录时间戳（秒）
    const userInfo = ref<UserVo>(() => {
      const raw = getCookie('userInfo')
      if (!raw) {
        return {} as UserVo
      }
      try {
        return JSON.parse(raw) as UserVo
      }
      catch {
        // cookie 可能被截断/污染，避免阻塞整个应用启动
        removeCookie('userInfo')
        return {} as UserVo
      }
    })
    const showLoginModal = ref(false)
    const showVipModal = ref(false)
    const needBindMobile = ref(false)
    // 微信扫码登录未绑定手机号时，后端会返回 userId；用于后续 bindMobileApi 绑定入参
    const pendingBindUserId = ref<string>('')
    const isLogin = computed(() => {
      return token.value !== ''
    })

    // 检查 token 是否过期（使用函数，每次调用都会执行）
    const isTokenExpired = () => {
      if (!expireTime.value || !loginTime.value) {
        return true
      }
      // 计算当前时间与登录时间的差值（秒）
      const now = Math.floor(Date.now() / 1000) // 直接获取当前时间
      const login = Number.parseInt(loginTime.value) // 登录时间戳（秒）
      const tokenValidity = Number.parseInt(expireTime.value) // token有效期（秒）

      // 如果当前时间 - 登录时间 >= token有效期，则token已过期
      return (now - login) >= tokenValidity
    }

    // 检查 token 是否有效（未过期且存在）
    const isTokenValid = computed(() => {
      return token.value !== '' && !isTokenExpired()
    })

    // 第三方扫码登录
    const accessToken = (code: string) => {
      // 从 cookie 中获取邀请码
      const inviteCode = getCookie('inviteCode')
      const tokenData: any = {
        type: 'wechat',
        code,
      }
      if (inviteCode) {
        tokenData.invite = inviteCode
      }

      authAccessToken(tokenData).then((res: any) => {
        if (res.code === 0) {
          // 判断是否绑定手机号，如果没有绑定手机号，则显示绑定手机号的面板
          if (!res.data.bindMobile) {
            // 未绑定手机号，通知 login.vue 切换成绑定面板
            needBindMobile.value = true
            // 保持登录弹窗打开（有些场景外部可能会尝试关闭弹窗）
            showLoginModal.value = true
            // 记录后端返回的 userId，供绑定手机号时使用
            pendingBindUserId.value = res.data.userId || ''
            // 不执行后续步骤，直接返回
            return
          }
          // 已绑定手机号，继续执行登录逻辑
          const currentTime = Math.floor(Date.now() / 1000) // 当前时间戳（秒）
          // 所有信息存储到 cookie，实现子域名共享，同时更新 ref 确保响应式
          const tokenValue = res.data.accessToken
          setCookie('satoken', tokenValue)
          setCookie('userId', res.data.userId)
          setCookie('expireTime', res.data.expireTime)
          setCookie('loginTime', currentTime.toString())
          token.value = tokenValue
          userId.value = res.data.userId
          expireTime.value = res.data.expireTime
          loginTime.value = currentTime.toString()
          showLoginModal.value = false
          // 登录成功后清除邀请码（已使用）
          if (inviteCode) {
            removeCookie('inviteCode')
          }
          // eslint-disable-next-line ts/no-use-before-define
          getMemberInfo()
        }
        else {
          // eslint-disable-next-line ts/no-use-before-define
          requestLogout()
        }
      })
    }

    // 更新用户token相关信息
    const updateAccessToken = (res: AuthSmsLoginRes) => {
      const currentTime = Math.floor(Date.now() / 1000) // 当前时间戳（秒）
      // 所有信息存储到 cookie，实现子域名共享，同时更新 ref 确保响应式
      const tokenValue = res.accessToken
      setCookie('satoken', tokenValue)
      setCookie('userId', res.userId)
      setCookie('expireTime', res.expireTime.toString())
      setCookie('loginTime', currentTime.toString())
      token.value = tokenValue
      userId.value = res.userId
      expireTime.value = res.expireTime.toString()
      loginTime.value = currentTime.toString()
      showLoginModal.value = false
      // eslint-disable-next-line ts/no-use-before-define
      getMemberInfo()
    }

    // 获取会员信息
    const getMemberInfo = () => {
      getProfileApi().then((res: any) => {
        if (res.code === 0) {
          avatar.value = res.data.avatar
          nickname.value = res.data.nickname
          userInfo.value = res.data
          // 所有信息存储到 cookie，实现子域名共享
          setCookie('userInfo', JSON.stringify(res.data))
          setCookie('avatar', avatar.value)
          setCookie('nickname', nickname.value)
        }
        else {
          // eslint-disable-next-line ts/no-use-before-define
          requestLogout()
        }
      })
    }

    // 手机登录
    const smsLogin = (mobile: string, code: string) => {
      // 从 cookie 中获取邀请码
      const inviteCode = getCookie('inviteCode')
      const loginData: any = { mobile, code }
      if (inviteCode) {
        loginData.invite = inviteCode
      }

      authSmsLogin(loginData).then((res: any) => {
        if (res.code === 0) {
          const currentTime = Math.floor(Date.now() / 1000) // 当前时间戳（秒）
          // 所有信息存储到 cookie，实现子域名共享，同时更新 ref 确保响应式
          const tokenValue = res.data.accessToken
          setCookie('account', mobile)
          setCookie('satoken', tokenValue)
          setCookie('userId', res.data.userId)
          setCookie('expireTime', res.data.expireTime)
          setCookie('loginTime', currentTime.toString())
          account.value = mobile
          token.value = tokenValue // 更新 ref，触发响应式更新
          userId.value = res.data.userId
          expireTime.value = res.data.expireTime
          loginTime.value = currentTime.toString()
          showLoginModal.value = false
          // 登录成功后清除邀请码（已使用）
          if (inviteCode) {
            removeCookie('inviteCode')
          }
          getMemberInfo()
        }
        else {
          // eslint-disable-next-line ts/no-use-before-define
          requestLogout()
          faToast.error(res.msg || '登录失败', { duration: 2000 })
        }
      })
    }

    // 内部登录：账号 + 密码（账号体系复用工作台，由管理员统一开号）
    const passwordLogin = (accountName: string, password: string) => {
      return authPasswordLogin({ account: accountName, password }).then((res: any) => {
        if (res.code === 0) {
          const currentTime = Math.floor(Date.now() / 1000) // 当前时间戳（秒）
          // 所有信息存储到 cookie，实现子域名共享，同时更新 ref 确保响应式
          const tokenValue = res.data.accessToken
          setCookie('account', accountName)
          setCookie('satoken', tokenValue)
          setCookie('userId', res.data.userId)
          setCookie('expireTime', res.data.expireTime)
          setCookie('loginTime', currentTime.toString())
          account.value = accountName
          token.value = tokenValue // 更新 ref，触发响应式更新
          userId.value = res.data.userId
          expireTime.value = res.data.expireTime
          loginTime.value = currentTime.toString()
          showLoginModal.value = false
          getMemberInfo()
          return true
        }
        faToast.error(res.msg || '登录失败', { duration: 2000 })
        return false
      })
    }

    // 手动登出
    const logout = () => {
      try {
        authLogout().then(() => {

        })
      }
      finally {
        // eslint-disable-next-line ts/no-use-before-define
        requestLogout()
      }
    }

    // 从 cookie 同步到 store（解决首次打开页面时 cookie 已存在但 store 仍为空的情况）
    const syncFromCookie = () => {
      const t = getCookie('satoken')
      if (t) {
        token.value = t
        account.value = getCookie('account') ?? ''
        avatar.value = getCookie('avatar') ?? ''
        nickname.value = getCookie('nickname') ?? ''
        userId.value = getCookie('userId') ?? ''
        expireTime.value = getCookie('expireTime') ?? ''
        loginTime.value = getCookie('loginTime') ?? ''
        const u = getCookie('userInfo')
        if (u) {
          try {
            userInfo.value = JSON.parse(u)
          }
          catch {
            userInfo.value = {}
          }
        }
      }
    }

    // 登出后清除状态
    const requestLogout = () => {
      // 删除所有 cookie 中的用户信息
      removeCookie('account')
      removeCookie('satoken')
      removeCookie('userId')
      removeCookie('expireTime')
      removeCookie('loginTime')
      removeCookie('userInfo')
      removeCookie('nickname')
      removeCookie('avatar')
      // 清空所有 ref，触发响应式更新
      account.value = ''
      token.value = ''
      avatar.value = ''
      userId.value = ''
      nickname.value = ''
      expireTime.value = ''
      loginTime.value = ''
      userInfo.value = {}
      // 若当前在个人中心或设置页，退出后跳转到创作页
      try {
        const router = useRouter()
        const route = useRoute()
        if (route.path === '/personCenter' || route.path.startsWith('/settings')) {
          router.push('/creative')
        }
      }
      catch {
        // 非组件上下文中调用时可能无 router，忽略
      }
    }

    return {
      account,
      token,
      avatar,
      nickname,
      userId,
      expireTime,
      loginTime,
      userInfo,
      isLogin,
      isTokenExpired,
      isTokenValid,
      showLoginModal,
      showVipModal,
      needBindMobile,
      pendingBindUserId,
      accessToken,
      updateAccessToken,
      smsLogin,
      passwordLogin,
      getMemberInfo,
      logout,
      requestLogout,
      syncFromCookie,
    }
  },
)
